// design/organisms/SidebarNavigation.js
import React, { useState, createContext, useContext, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Tooltip, Collapse, Menu, MenuItem } from '@mui/material';
import { themeConstants } from '../theme';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import LogoutIcon from '@mui/icons-material/Logout';

// Icons
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';

// Sidebar context for collapsed state
const SidebarContext = createContext({ collapsed: false, setCollapsed: () => {} });

// Sidebar dimensions
const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 64;

// Main sidebar container
const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed'
})(({ collapsed }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  position: 'fixed',
  left: 0,
  top: 0,
  zIndex: 1200,
  transition: 'width 0.2s ease',
  width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
  backgroundColor: themeConstants.colors.sidebar.background,
}));

// Header section with logo and collapse button
const HeaderSection = styled(Box, {
  shouldForwardProp: (prop) => !['collapsed', 'tenantSlug'].includes(prop)
})(({ collapsed, tenantSlug }) => ({
  padding: collapsed ? '12px 8px' : '12px 16px',
  borderBottom: `1px solid ${tenantSlug === 'solo' ? '#7c4a1e' : themeConstants.colors.sidebar.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: collapsed ? 'center' : 'space-between',
  minHeight: 56,
}));

// Logo container
const LogoContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed'
})(({ collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  overflow: 'hidden',
  opacity: collapsed ? 0 : 1,
  width: collapsed ? 0 : 'auto',
  transition: 'opacity 0.2s ease, width 0.2s ease',
}));

// Logo icon box
const LogoIcon = styled(Box)({
  width: 32,
  height: 32,
  borderRadius: 8,
  backgroundColor: themeConstants.colors.sidebar.accent,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    color: '#fff',
    fontSize: 20,
  },
});

// Logo text
const LogoText = styled('span')({
  color: '#ffffff',
  fontSize: '1.125rem',
  fontWeight: 600,
  letterSpacing: '-0.025em',
  whiteSpace: 'nowrap',
});

// Collapse button
const CollapseButton = styled(Box)({
  width: 28,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 6,
  cursor: 'pointer',
  color: themeConstants.colors.sidebar.text,
  transition: 'all 0.15s ease',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: themeConstants.colors.sidebar.textHover,
  },
  '& svg': {
    fontSize: 18,
  },
});

// Navigation list
const NavList = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '8px 0',
  '&::-webkit-scrollbar': {
    width: 4,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: themeConstants.colors.sidebar.divider,
    borderRadius: 2,
  },
});

// Nav item container
const NavItemContainer = styled(Box, {
  shouldForwardProp: (prop) => !['active', 'collapsed'].includes(prop)
})(({ active, collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: collapsed ? '10px 0' : '10px 16px',
  justifyContent: collapsed ? 'center' : 'flex-start',
  cursor: 'pointer',
  color: active ? themeConstants.colors.sidebar.textActive : themeConstants.colors.sidebar.text,
  backgroundColor: active ? themeConstants.colors.sidebar.backgroundHover : 'transparent',
  borderLeft: active ? `3px solid ${themeConstants.colors.sidebar.accent}` : '3px solid transparent',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: themeConstants.colors.sidebar.backgroundHover,
    color: themeConstants.colors.sidebar.textHover,
  },
}));

// Nav item icon
const NavItemIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed'
})(({ collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: collapsed ? 0 : 12,
  '& svg': {
    fontSize: 20,
  },
}));

// Nav item label
const NavItemLabel = styled('span')({
  flex: 1,
  fontSize: '0.875rem',
  fontWeight: 400,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

// Nav item chevron
const NavItemChevron = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 8,
  '& svg': {
    fontSize: 18,
    transition: 'transform 0.2s ease',
  },
});

// Sub-nav item
const SubNavItem = styled(Box, {
  shouldForwardProp: (prop) => !['active', 'collapsed'].includes(prop)
})(({ active, collapsed }) => ({
  display: collapsed ? 'none' : 'flex',
  alignItems: 'center',
  padding: '8px 16px 8px 48px',
  cursor: 'pointer',
  color: active ? themeConstants.colors.sidebar.textActive : themeConstants.colors.sidebar.text,
  fontSize: '0.813rem',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: themeConstants.colors.sidebar.backgroundHover,
    color: themeConstants.colors.sidebar.textHover,
  },
}));

// Section divider
const SectionDivider = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed'
})(({ collapsed }) => ({
  height: 1,
  backgroundColor: themeConstants.colors.sidebar.divider,
  margin: collapsed ? '8px 8px' : '8px 16px',
}));

// Section label
const SectionLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed'
})(({ collapsed }) => ({
  padding: collapsed ? '16px 8px 8px' : '16px 16px 8px',
  fontSize: '0.688rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: themeConstants.colors.sidebar.text,
  opacity: 0.6,
  textAlign: collapsed ? 'center' : 'left',
  display: collapsed ? 'none' : 'block',
}));

// Bottom section (user)
const BottomSection = styled(Box)({
  borderTop: `1px solid ${themeConstants.colors.sidebar.divider}`,
  padding: '12px 0',
});

// User item
const UserItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed'
})(({ collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: collapsed ? '8px 0' : '8px 16px',
  justifyContent: collapsed ? 'center' : 'flex-start',
  cursor: 'pointer',
  color: themeConstants.colors.sidebar.text,
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: themeConstants.colors.sidebar.backgroundHover,
    color: themeConstants.colors.sidebar.textHover,
  },
}));

// User avatar
const UserAvatar = styled(Box)({
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: themeConstants.colors.sidebar.accent,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#ffffff',
  fontSize: '0.75rem',
  fontWeight: 600,
  flexShrink: 0,
});

// User name
const UserName = styled('span')({
  marginLeft: 12,
  fontSize: '0.875rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

/**
 * NavItem component - Individual navigation item
 */
const NavItem = ({ item, currentPath, onNavigate, collapsed }) => {
  const [expanded, setExpanded] = useState(false);
  const { setCollapsed } = useContext(SidebarContext);

  const isActive = currentPath === item.href ||
    (item.children && item.children.some(child => currentPath === child.href));
  const hasChildren = item.children && item.children.length > 0;
  const Icon = item.icon;

  const handleClick = () => {
    if (hasChildren) {
      if (collapsed) {
        setCollapsed(false);
        setExpanded(true);
      } else {
        setExpanded(!expanded);
      }
    } else if (item.href) {
      if (onNavigate) {
        onNavigate(item.href);
      } else {
        window.location.href = item.href;
      }
    }
  };

  const navContent = (
    <>
      <NavItemContainer active={isActive} collapsed={collapsed} onClick={handleClick}>
        <NavItemIcon collapsed={collapsed}>
          {Icon && <Icon />}
        </NavItemIcon>
        {!collapsed && (
          <>
            <NavItemLabel>{item.label}</NavItemLabel>
            {hasChildren && (
              <NavItemChevron>
                {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </NavItemChevron>
            )}
          </>
        )}
      </NavItemContainer>
      {hasChildren && !collapsed && (
        <Collapse in={expanded}>
          {item.children.map((child) => (
            <SubNavItem
              key={child.id}
              active={currentPath === child.href}
              collapsed={collapsed}
              onClick={() => {
                if (onNavigate) {
                  onNavigate(child.href);
                } else {
                  window.location.href = child.href;
                }
              }}
            >
              {child.label}
            </SubNavItem>
          ))}
        </Collapse>
      )}
    </>
  );

  if (collapsed) {
    return (
      <Tooltip title={item.label} placement="right" arrow>
        {navContent}
      </Tooltip>
    );
  }

  return navContent;
};

/**
 * SidebarNavigation component - Unified sidebar navigation
 */
const SIDEBAR_STORAGE_KEY = 'sidebar-collapsed-v3';

const SidebarNavigation = ({
  navItems = [],
  currentPath = '',
  onNavigate,
  logoSrc,
  logoText = 'TMS',
  user = { name: 'User', initials: 'U' },
  defaultCollapsed = true,
  tenantSlug = '',
  onProfileClick,
  onLogoutClick,
}) => {
  // Initialize from localStorage or default
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      if (stored !== null) {
        return stored === 'true';
      }
    }
    return defaultCollapsed;
  });

  // User menu state
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const userMenuOpen = Boolean(userMenuAnchor);

  const handleUserClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleProfileClick = () => {
    handleUserMenuClose();
    if (onProfileClick) {
      onProfileClick();
    } else {
      window.location.href = '/profile';
    }
  };

  const handleLogoutClick = () => {
    handleUserMenuClose();
    if (onLogoutClick) {
      onLogoutClick();
    } else {
      window.location.href = '/logout/';
    }
  };

  // Persist to localStorage when state changes
  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  // Listen for storage events (from other components like LoadSchedule)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === SIDEBAR_STORAGE_KEY && e.newValue !== null) {
        setCollapsed(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Sync collapsed state with body class for CSS
  useEffect(() => {
    if (collapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
    return () => {
      document.body.classList.remove('sidebar-collapsed');
    };
  }, [collapsed]);

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
      <SidebarContainer collapsed={collapsed}>
        {/* Header with Logo and Collapse Button */}
        <HeaderSection collapsed={collapsed} tenantSlug={tenantSlug}>
          {collapsed ? (
            <Tooltip title="Expand sidebar" placement="right">
              <CollapseButton onClick={() => setCollapsed(false)}>
                <ChevronRightIcon />
              </CollapseButton>
            </Tooltip>
          ) : (
            <>
              <LogoContainer collapsed={collapsed}>
                {logoSrc ? (
                  <img src={logoSrc} alt="Logo" style={{ height: 28 }} />
                ) : (
                  <LogoIcon>
                    <LocalShippingOutlinedIcon />
                  </LogoIcon>
                )}
                <LogoText>{logoText}</LogoText>
              </LogoContainer>
              <CollapseButton onClick={() => setCollapsed(true)}>
                <ChevronLeftIcon />
              </CollapseButton>
            </>
          )}
        </HeaderSection>

        {/* Navigation Items */}
        <NavList>
          {navItems.map((item) => {
            if (item.type === 'divider') {
              return <SectionDivider key={item.id} collapsed={collapsed} />;
            }
            if (item.type === 'section') {
              return <SectionLabel key={item.id} collapsed={collapsed}>{item.label}</SectionLabel>;
            }
            return (
              <NavItem
                key={item.id}
                item={item}
                currentPath={currentPath}
                onNavigate={onNavigate}
                collapsed={collapsed}
              />
            );
          })}
        </NavList>

        {/* User Avatar at Bottom */}
        <BottomSection>
          <Tooltip title={user.name} placement="right">
            <UserItem collapsed={true} onClick={handleUserClick}>
              <UserAvatar>{user.initials}</UserAvatar>
            </UserItem>
          </Tooltip>
          <Menu
            anchorEl={userMenuAnchor}
            open={userMenuOpen}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            PaperProps={{
              sx: {
                mb: 1,
                minWidth: 160,
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              },
            }}
          >
            <MenuItem onClick={handleProfileClick}>
              <PersonOutlinedIcon sx={{ mr: 1.5, fontSize: 20 }} />
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogoutClick} sx={{ color: 'error.main' }}>
              <LogoutIcon sx={{ mr: 1.5, fontSize: 20 }} />
              Logout
            </MenuItem>
          </Menu>
        </BottomSection>
      </SidebarContainer>
    </SidebarContext.Provider>
  );
};

// Export sidebar dimensions for layout calculations
export const sidebarDimensions = {
  width: SIDEBAR_WIDTH,
  collapsedWidth: SIDEBAR_COLLAPSED_WIDTH,
};

export { SidebarNavigation, SidebarContext };
export default SidebarNavigation;
